
import proj4 from "proj4";

import { AbandonedVehicle, AerialImage, DatasetImage, ObbDet } from "../api/Application";

export type LatLng      = [lat:     number, lng:      number] & { readonly _brand: "LatLng"      };
export type LngLat      = [lng:     number, lat:      number] & { readonly _brand: "LngLat"      };
export type Pixel       = [x:       number, y:        number] & { readonly _brand: "Pixel"       };
export type RectSize    = [w:       number, h:        number] & { readonly _brand: "RectSize"    };
export type UtmPoint    = [easting: number, northing: number] & { readonly _brand: "UtmPoint"    };
export type PixelOffset = [x:       number, y:        number] & { readonly _brand: "PixelOffset" };

//w altitude
export type LatLngAlt = [lat:     number, lng:      number, alt: number] & { readonly _brand: "LatLngAlt" };
export type LngLatAlt = [lng:     number, lat:      number, alt: number] & { readonly _brand: "LngLatAlt" };

const utm18n = "+proj=utm +zone=18 +datum=WGS84 +units=m +no_defs";
const wgs84  = "+proj=longlat +datum=WGS84 +no_defs";
const xform  = proj4( utm18n, wgs84 );

for( let zone = 1; zone <= 60; zone++ ) {
    proj4.defs( `EPSG:${32600 + zone}`, `+proj=utm +zone=${zone} +datum=WGS84 +units=m +no_defs +type=crs`        );
    proj4.defs( `EPSG:${32700 + zone}`, `+proj=utm +zone=${zone} +south +datum=WGS84 +units=m +no_defs +type=crs` );
}

// step 1: convert pixel coordinates to utm meters
export function pixelToUtm( x: number, y: number, image: AerialImage ): UtmPoint {
    const utmX = image.originX + ( x * image.pixelWidth  );
    const utmY = image.originY + ( y * image.pixelHeight );
    return [utmX, utmY] as UtmPoint;
}

// step 2: convert utm meters to lat/lon
export function utmToLatLng( utmX: number, utmY: number, image: AerialImage ): LatLng {
    const [lng, lat] = xform.forward( [utmX, utmY] );
    return [lat, lng] as LatLng;
}

// complete conversion: pixels -> utm -> lat/lon
export function pixelToLatLng( x: number, y: number, image: AerialImage): LatLng {
    const [utmX, utmY] = pixelToUtm( x, y, image );
    return utmToLatLng( utmX, utmY, image );
}

export function latLngToPixel( lat: number, lng: number, image: AerialImage ): Pixel {
    const [utmX, utmY] = xform.inverse( [lng, lat] );
    const pixelX = ( utmX - image.originX ) / image.pixelWidth;
    const pixelY = ( utmY - image.originY ) / image.pixelHeight;
    return [pixelX, pixelY] as Pixel;
}

export function utmToPixel( easting: number, northing: number, image: AerialImage ): Pixel {
    const pixelX = ( easting  - image.originX ) / image.pixelWidth;
    const pixelY = ( northing - image.originY ) / image.pixelHeight;
    return [pixelX, pixelY] as Pixel;
}

export function createImageProjection( image: DatasetImage | AerialImage ) {
    if( image.epsgCode == null ) {
        // throw new Error( "Image has no EPSG code" );
        return undefined;
    }
    // proj4 has EPSG:4326 and 3857 built in; for others register by code.
    // Easiest: use the "EPSG:XXXX" string form — proj4 will look it up if defined,
    // otherwise you"ll need to register a definition.
    const from = `EPSG:${image.epsgCode}`;
    const xform = proj4( from, wgs84 );
    return {
        pixelToLatLng( x: number, y: number ): LatLng {
            const utmX = image.originX! + x * image.pixelWidth!;
            const utmY = image.originY! + y * image.pixelHeight!;
            const [lng, lat] = xform.forward( [utmX, utmY] );
            return [lat, lng] as LatLng;
        },
        latLngToPixel( lat: number, lng: number ): Pixel {
            const [utmX, utmY] = xform.inverse( [lng, lat] );
            return [
                ( utmX - image.originX! ) / image.pixelWidth!,
                ( utmY - image.originY! ) / image.pixelHeight!,
            ] as Pixel;
        },
    };
}

export type ImageProjection = ReturnType<typeof createImageProjection>;